import { useEffect, useState, useRef } from "react";
import "./coco.css";
import { setLoader } from "../../../features/LoaderSlice";
import useConsentFragment from "../../../hooks/useConsentFragment";
import { useDispatch, useSelector } from "react-redux";
import sample from "./sample.json";
import { FormattedMessage, useIntl } from "react-intl";
import ClipLoader from "react-spinners/ClipLoader";
import CocoHeader from "./CocoHeader";
import CocoListing from "./CocoListing";
import CocoSkeleton from "./CocoSkeleton";
import {
  addCocoConfig,
  addCocoConfigLabel,
  addControllerCode,
  addCustomerScope,
  addLanguage,
} from "../../../features/CocoConfigurationSlice";
import CustomSelect from "../../CustomSelect";
const Coco = ({ loading }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const controller_code = useSelector((state) => state.controllers.controller);
  const customer_scope = useSelector((state) => state.controller.scope);
  const countryLanguages = useSelector(
    (state) => state.controller.countryLanguages
  );
  const configLabel = useSelector(
    (state) => state.cocoConfigDetailsReducer.cocoConfigLabel
  );
  const config = useSelector(
    (state) => state.cocoConfigDetailsReducer.cocoConfig
  );
  const cocoConfigControllerCode = useSelector(
    (state) => state.cocoConfigDetailsReducer.controllerCode
  );
  const cocoConfigCustomerScope = useSelector(
    (state) => state.cocoConfigDetailsReducer.customerScope
  );
  const cocoConfigLanguage = useSelector(
    (state) => state.cocoConfigDetailsReducer.language
  );
  const { getVersionInfo } = useConsentFragment();

  const [isOpenRequiredFields, setIsOpenRequiredFields] = useState(false);
  const [cocoConfig, setCocoConfig] = useState({});
  const [cocoConfigLabel, setCocoConfigLabel] = useState({});
  const [langLoader, setLangLoader] = useState(true);
  const [languageReady, setLanguageReady] = useState(false);
  const [lang, setLang] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const lastFetchedRef = useRef(null);
  useEffect(() => {
    if (countryLanguages && countryLanguages.length > 0) {
      const defaultOption = countryLanguages[0] || null;
      const defaultLang = defaultOption?.value;
      dispatch(addLanguage(defaultLang));
      setLangLoader(false);
      setLanguageReady(true);
      setLang(defaultLang);
      setSelectedOption(defaultOption ? { ...defaultOption } : null);
    }
  }, [countryLanguages, controller_code, customer_scope]);
  useEffect(() => {
    if (
      configLabel &&
      config &&
      controller_code === cocoConfigControllerCode &&
      customer_scope === cocoConfigCustomerScope &&
      lang === cocoConfigLanguage
    ) {
      setCocoConfigLabel(configLabel);
      setCocoConfig(config);
    }
  }, [
    configLabel,
    config,
    controller_code,
    customer_scope,
    cocoConfigControllerCode,
    cocoConfigCustomerScope,
    lang,
    cocoConfigLanguage,
  ]);

  useEffect(() => {
    if (
      controller_code &&
      customer_scope &&
      selectedOption?.value &&
      languageReady
    ) {
      fetchConfigForOption(selectedOption);
    }
  }, [controller_code, customer_scope, selectedOption, languageReady]);

  const getConfigFromFragment = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getVersionInfo(controller, scope, language);
    return data;
  };
  const getCocoConfigDetails = async (obj) => {
    const key = `${obj.controller}||${obj.scope}||${obj.language}`;
    if (lastFetchedRef.current === key) return; // already fetched
    lastFetchedRef.current = key;
    if (controller_code && customer_scope) {
      try {
        const configData = await getConfigFromFragment(obj);
        setCocoConfig(configData || {});
        dispatch(addCocoConfig(configData || {}));
        await getCocoConfigLabels();
      } catch (e) {
        setCocoConfig({});
      }
    }
  };

  const fetchConfigForOption = (opt) => {
    if (!opt?.value) return;
    const obj = {
      controller: controller_code,
      scope: customer_scope || "hcp",
      language: opt.value,
    };
    getCocoConfigDetails(obj);
  };
  const getCocoConfigLabels = async () => {
    dispatch(setLoader({ loading: true, label: "" }));
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_DOMAIN
        }/${controller_code}/${customer_scope?.toLowerCase()}/coco_config_labels.json`
      );

      const configLabels = await response.json();
      setCocoConfigLabel(configLabels);
      dispatch(addCocoConfigLabel(configLabels));
      dispatch(addControllerCode(controller_code));
      dispatch(addCustomerScope(customer_scope));
      dispatch(addLanguage(selectedOption?.value));

      dispatch(setLoader({ loading: false, label: "" }));
    } catch {
      setCocoConfigLabel(sample);
      dispatch(addCocoConfigLabel(sample));
      dispatch(addControllerCode(controller_code));
      dispatch(addCustomerScope(customer_scope));
      dispatch(addLanguage(selectedOption?.value));
      dispatch(setLoader({ loading: false, label: "" }));
    }
  };
  if (loading) {
    return <CocoSkeleton />;
  }
  return (
    <section className="container-fluid cococonfig__content pt-3">
      <div className="cococonfig__content-lists">
        <div
          className={`d-flex justify-content-end align-items-center ${
            langLoader && "pe-5"
          }`}
        >
          {langLoader ? (
            <ClipLoader size={40} />
          ) : (
            <CustomSelect
              options={countryLanguages}
              placeHolder={intl.formatMessage({
                id: "id.new-placeholder.language",
                defaultMessage: "Select Language",
              })}
              defaultValue={selectedOption}
              value={selectedOption}
              onChange={(opt) => {
                const newOpt = opt ? { ...opt } : opt;
                setSelectedOption(newOpt);
                fetchConfigForOption(newOpt);
              }}
              variant="select-secondary"
              className={`justify-content-end flex-row`}
              dispatchValue={true}
            />
          )}
        </div>
        {!languageReady ? (
          <CocoSkeleton />
        ) : (
          <>
            <CocoHeader />
            {cocoConfig?.error ? (
              <div className="fs-5 py-2 text-center fw-bold text-danger">
                <FormattedMessage
                  id={"cococonfig.errorMessage"}
                  defaultMessage="No configuration available"
                />
              </div>
            ) : (
              <CocoListing
                cocoConfig={cocoConfig}
                cocoConfigLabel={cocoConfigLabel}
                isOpenRequiredFields={isOpenRequiredFields}
                setIsOpenRequiredFields={setIsOpenRequiredFields}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Coco;